import { Calendar, MapPin, DollarSign, Brain, Users, Zap } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import { InterestDialog } from "@/components/dialogs/InterestDialog";
import { ASSETS } from "@/lib/constants";
import { useSEO } from "@/hooks/useSEO";

const FOCUS = [
  { icon: Zap, title: "Development", desc: "Maximize ball touches and reps so young players grow fast." },
  { icon: Brain, title: "Decision-Making", desc: "Read the game and make smart choices in a small-sided setting." },
  { icon: Users, title: "Teamwork", desc: "Learn spacing, passing and how to play together the right way." },
];

const DETAILS = [
  { icon: Calendar, label: "Dates", value: "Winter — TBA" },
  { icon: MapPin, label: "Location", value: "Norwalk, CT — TBA" },
  { icon: DollarSign, label: "Cost", value: "TBA" },
];

export default function Winter3v3() {
  useSEO({
    title: "Winter 3v3 3rd Grade Basketball Development | CT BLAZERS Norwalk, CT",
    description:
      "CT BLAZERS winter 3v3 development program built specifically for 3rd grade players in Norwalk, CT. Emphasizing development, decision-making, teamwork and competition.",
    keywords: "Youth Basketball Norwalk CT, 3v3 basketball Connecticut, 3rd grade basketball development, CT Blazers 3v3",
    path: "/winter-3v3",
  });

  return (
    <div className="bg-ink-900 min-h-screen">
      <Navbar />
      <PageHeader
        eyebrow="3rd Grade Development Program"
        title="Winter"
        highlight="3v3"
        image={ASSETS.clinicHuddle}
        subtitle="A winter 3v3 development program designed specifically for 3rd grade players — built to develop skills, teach the game, and build confidence through decision-making, teamwork and competition."
      >
        <InterestDialog category="3v3" program="Winter 3v3 (3rd Grade)" testId="v3">
          <Button
            data-testid="v3-register-btn"
            className="bg-blaze-orange hover:bg-blaze-orangeBright text-ink-900 font-bold uppercase tracking-wide rounded-full h-12 px-8 shadow-[0_10px_30px_rgba(246,140,31,0.4)] transition-all hover:-translate-y-1"
          >
            Register Interest
          </Button>
        </InterestDialog>
      </PageHeader>

      <section className="py-16 sm:py-24 bg-ink-800">
        <div className="container-x">
          <div className="grid gap-6 sm:grid-cols-3">
            {FOCUS.map((f, i) => (
              <Reveal key={f.title} delay={i * 0.08}>
                <div className="hover-lift rounded-2xl border border-white/10 bg-ink-700 p-6 h-full hover:border-blaze-blueLight/50">
                  <div className="grid h-11 w-11 place-items-center rounded-lg bg-blaze-blue/15 text-blaze-blueLight">
                    <f.icon size={22} />
                  </div>
                  <h3 className="mt-4 font-heading text-2xl tracking-wide text-white uppercase">{f.title}</h3>
                  <p className="mt-2 text-sm text-white/60 leading-relaxed">{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-10 grid gap-6 lg:grid-cols-2">
            <Reveal>
              <div className="rounded-2xl border border-white/10 bg-ink-700/60 p-8 h-full">
                <h2 className="font-display font-black uppercase tracking-tight text-3xl text-white">Program Details</h2>
                <div className="mt-6 space-y-4">
                  {DETAILS.map((d) => (
                    <div key={d.label} className="flex items-center gap-4 rounded-xl border border-white/10 bg-ink-700/60 p-4">
                      <div className="grid h-11 w-11 place-items-center rounded-lg bg-blaze-blue/15 text-blaze-blueLight">
                        <d.icon size={20} />
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wider text-white/50">{d.label}</p>
                        <p className="font-heading text-2xl tracking-wide text-white">{d.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-blaze-blue/20 to-ink-700 p-8 h-full flex flex-col justify-between">
                <div>
                  <h3 className="font-heading text-3xl tracking-wide text-white">Save Your Spot</h3>
                  <p className="mt-3 text-white/70 leading-relaxed">
                    Register your interest and we'll follow up with dates, location, cost and registration details as soon
                    as they're finalized.
                  </p>
                </div>
                <InterestDialog category="3v3" program="Winter 3v3 (3rd Grade)" testId="v3-bottom">
                  <Button
                    data-testid="v3-register-btn-2"
                    className="mt-6 bg-blaze-orange hover:bg-blaze-orangeBright text-ink-900 font-bold uppercase tracking-wide rounded-full h-12"
                  >
                    Register Interest
                  </Button>
                </InterestDialog>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
